import { useState } from 'react';
import { Pane, Button, Text, Avatar, Badge, Tooltip, IconButton, TickIcon, TimeIcon, Heading, DownloadIcon } from 'evergreen-ui';
import { calcDeduzioneNottePassiva } from '../nottePassiva';
import TurniPdfPreviewModal from '../TurniPdfPreviewModal';

export default function VistaSettimanale({
  mockDays,
  selectedWeek,
  setSelectedWeek,
  collaboratorsList,
  turniData,
  managerFerieList,
  isDirector,
  getInitials,
  getBadgeProps,
  handleTimbra
}) {
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const weeks = [];
  for (let i = 0; i < mockDays.length; i += 7) {
    weeks.push(mockDays.slice(i, i + 7));
  }
  const weekDays = weeks[selectedWeek] || [];

  // Stats calcolo
  const statsOverview = collaboratorsList.map(user => {
    const uName = `${user.nome} ${user.cognome}`.trim();
    const userShifts = turniData.filter(t => t.operator_name === uName);        

    let expectedHours = 0;
    let workedHours = 0;
    let ferieHours = 0;
    let permessiHours = 0;
    let malattiaHours = 0;
    let overtimeHours = 0;

    userShifts.forEach(t => {
      let duration = 0;
      if (t.start_time && t.end_time) {
        const [sH, sM] = t.start_time.split(':').map(Number);
        const [eH, eM] = t.end_time.split(':').map(Number);
        duration = (eH + eM/60) - (sH + sM/60);

        if (eH === 23 && eM >= 58 && sH !== 23) duration += (24 - (eH + eM/60));

        if (duration < 0) duration += 24;
      }

      if (t.shift_type === 'ferie') {
        ferieHours += duration > 1 ? duration : parseFloat(user.ore_giornaliere_previste || 0);
      } else if (t.shift_type === 'permesso') {
        permessiHours += duration > 0 ? duration : parseFloat(user.ore_giornaliere_previste || 0);
      } else if (t.shift_type === 'malattia') {
        malattiaHours += duration > 1 ? duration : parseFloat(user.ore_giornaliere_previste || 0);
      } else {
        const counted = duration - calcDeduzioneNottePassiva(t);
        expectedHours += counted;
        if (t.status === 'timbrato') workedHours += counted;
        if (parseInt(t.is_overtime, 10) === 1) overtimeHours += counted;
      }
    });

    // Arrotonda per evitare decimali strani
    return {
      uName,
      oreMensiliContratto: parseFloat(user.ore_mensili_previste || 0),
      expectedHours: parseFloat(Number(expectedHours || 0).toFixed(1)),
      workedHours: parseFloat(Number(workedHours || 0).toFixed(1)),
      ferieHours: parseFloat(Number(ferieHours || 0).toFixed(1)),
      permessiHours: parseFloat(Number(permessiHours || 0).toFixed(1)),
      malattiaHours: parseFloat(Number(malattiaHours || 0).toFixed(1)),
      overtimeHours: parseFloat(Number(overtimeHours || 0).toFixed(1))
    };
  });
  return (
    <>
    <Pane border="default" borderRadius={4} minWidth={1000}>
      {/* Header Selezione Settimana */}
      <Pane padding={12} display="flex" gap={12} borderBottom="default" alignItems="center">
        {weeks.map((_, i) => (
          <Button key={i} appearance={selectedWeek === i ? 'primary' : 'default'} onClick={() => setSelectedWeek(i)}>
            Settimana {i + 1}
          </Button>
        ))}
        <Pane flex={1} />
        <Button iconBefore={DownloadIcon} onClick={() => setIsPdfModalOpen(true)}>
          Genera PDF
        </Button>
      </Pane>
      
      {/* Header Tabella */}
      <Pane display="flex" background="tint2" padding={12} borderBottom="default" alignItems="center">
        <Pane width={160}><Text fontWeight="bold">Operatore</Text></Pane>
        {weekDays.map((d, index) => {
          const giorniSettimana = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
          return (
            <Pane flex={1} key={d ? d.date : `empty-${index}`} textAlign="center">
              <Text size={300} fontWeight="bold">{d ? d.date : ''}</Text>
              {d && <Text size={300} color="muted" display="block">{giorniSettimana[index % 7]}</Text>}
            </Pane>
          );
        })}
      </Pane>

      {/* Righe Utenti */}
      {collaboratorsList.map(user => {
        const uName = `${user.nome} ${user.cognome}`.trim();
        return (
          <Pane display="flex" padding={8} borderBottom="muted" key={user.id} alignItems="center">
            <Pane width={160} display="flex" alignItems="center" gap={8} overflow="hidden">
              <Avatar name={getInitials(uName)} size={24} />
              <Text size={300} truncate>{uName}</Text>
            </Pane>

            {weekDays.map((d, index) => {
              if (!d) return <Pane flex={1} key={`empty-${index}`} textAlign="center" background="tint1" />;
              let content = <Text color="muted">-</Text>;

              const ls = managerFerieList.filter(leave => leave.operator_name === uName && leave.start_date <= d.fullDate && leave.end_date >= d.fullDate && leave.status === 'approvata');
              if (ls.length > 0) {
                  let timeStr = "";
                  if (['permesso', 'ferie'].includes((ls[0].leave_type || '').toLowerCase()) && ls[0].start_time && ls[0].start_time.substring(0,5) !== '00:00' && ls[0].end_time) {
                    timeStr = ` ${ls[0].start_time.substring(0,5)}-${ls[0].end_time.substring(0,5)}`;
                  }
                  const badgeInitial = (ls[0].leave_type || 'FER').substring(0,1).toUpperCase();
                  const badgeColor = isDirector ? (badgeInitial === 'M' ? 'red' : badgeInitial === 'P' ? 'yellow' : 'green') : 'green';
                  const badgeText = isDirector ? `${badgeInitial}${timeStr}` : `${(ls[0].leave_type || 'FER').substring(0,3).toUpperCase()}${timeStr}`;
                  content = <Badge color={badgeColor} isSolid>{badgeText}</Badge>;
              } else {
                  const ss = turniData.filter(shift => shift.operator_name === uName && shift.date === d.fullDate);
                  if (ss.length > 0) {
                      content = (
                          <Pane display="flex" flexDirection="column" alignItems="center" gap={4}>
                              {ss.map((s, i) => {
                                  const isAbsence = ['ferie', 'permesso', 'malattia'].includes(s.shift_type);
                                  const bp = getBadgeProps(s.shift_type);
                                  
                                  let timeStr = "";
                                  if (isAbsence && s.start_time && s.start_time.substring(0,5) !== '00:00' && s.end_time && ['permesso', 'ferie'].includes(s.shift_type)) {
                                    timeStr = ` ${s.start_time.substring(0,5)}-${s.end_time.substring(0,5)}`;
                                  }
                                  
                                  const badgeInitial = isAbsence ? (s.shift_type.substring(0,1).toUpperCase()) : '';
                                  const directorBadgeColor = badgeInitial === 'M' ? 'red' : badgeInitial === 'P' ? 'yellow' : 'green';
                                  
                                  let finalColor = bp.color;
                                  let finalIsSolid = !isAbsence;
                                  let displayText = `${s.start_time ? s.start_time.substring(0,5) : '?'} - ${s.end_time ? s.end_time.substring(0,5) : '?'}`;
                                  
                                  if (isAbsence && isDirector) {
                                      finalColor = directorBadgeColor;
                                      finalIsSolid = true;
                                      displayText = `${badgeInitial}${timeStr}`;
                                  } else if (isAbsence) {
                                      displayText = `${bp.text.substring(0,3).toUpperCase()}${timeStr}`;
                                      finalIsSolid = true;
                                  }

                                  const noteText = (s.notes && String(s.notes).trim() !== '' && String(s.notes) !== '0') ? s.notes : 'Nessuna nota';

                                  return (
                                    <Tooltip content={noteText} key={i}>        
                                      <Pane display="flex" alignItems="center" gap={4}>
                                          <Badge color={finalColor} isSolid={finalIsSolid} paddingX={6} style={{ textTransform: 'none', whiteSpace: 'normal', display: 'block', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {displayText} {s.is_overtime ? '(Str.)' : ''}
                                          </Badge>
                                          {s.status === 'timbrato' ? (
                                              <TickIcon color="success" size={12} title="Timbrato" />
                                          ) : (
                                              isDirector && !isAbsence && (
                                                <IconButton
                                                    icon={TimeIcon}
                                                    size="small"
                                                    appearance="minimal"
                                                    disabled={d.fullDate > new Date().toISOString().split('T')[0]}
                                                    onClick={(e) => { e.stopPropagation(); handleTimbra(s.id, d.fullDate); }}
                                                    title="Timbra turno"
                                                />
                                              )
                                          )}
                                      </Pane>
                                    </Tooltip>
                                  )
                              })}
                          </Pane>
                      )
                  }
              }

              return <Pane flex={1} textAlign="center" key={d.date} display="flex" justifyContent="center">{content}</Pane>;
            })}
          </Pane>
        );
      })}

      {/* Recap Statistiche in coda alla tabella */}
      <Pane padding={16} background="tint1">
        <Heading size={500} marginBottom={16}>Conteggio ore mensili</Heading>
        <Pane display="flex" flexDirection="column" gap={8}>
          <Pane display="grid" gridTemplateColumns="repeat(8, 1fr)" gap={8} paddingBottom={8} borderBottom="muted">
            <Text fontWeight="bold">Dipendente</Text>
            <Text fontWeight="bold">Ore da contratto</Text>
            <Text fontWeight="bold">Ore assegnate</Text>
            <Text fontWeight="bold">Ore svolte</Text>
            <Text fontWeight="bold">Straordinari</Text>
            <Text fontWeight="bold">Ferie</Text>
            <Text fontWeight="bold">Permessi</Text>
            <Text fontWeight="bold">Malattia</Text>
          </Pane>
          {statsOverview.map(s => (
            <Pane key={s.uName} display="grid" gridTemplateColumns="repeat(8, 1fr)" gap={8}>
              <Text truncate>{s.uName}</Text>
              <Text>{s.oreMensiliContratto}h</Text>
              <Text>{s.expectedHours}h</Text>
              <Text color={s.workedHours >= s.expectedHours ? 'success' : 'danger'} fontWeight="bold">{s.workedHours}h</Text>
              <Text>{s.overtimeHours}h</Text>
              <Text>{s.ferieHours}h</Text>
              <Text>{s.permessiHours}h</Text>
              <Text>{s.malattiaHours}h</Text>
            </Pane>
          ))}
        </Pane>
      </Pane>
    </Pane>
    <TurniPdfPreviewModal
      isOpen={isPdfModalOpen}
      onClose={() => setIsPdfModalOpen(false)}
      allDays={mockDays.filter(Boolean)}
      collaboratorsList={collaboratorsList}
      turniData={turniData}
      managerFerieList={managerFerieList}
    />
    </>
  );
}
